// Mirrors common/attachments.py — keep in sync. Server is authoritative; these
// values exist only to give the user immediate feedback before upload.

export const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

export const SUPPORTED_TEXT_MIMETYPES: ReadonlySet<string> = new Set([
  "text/plain",
  "text/csv",
  "text/html",
  "text/xml",
  "text/markdown",
  "text/x-python",
  "text/x-yaml",
  "text/javascript",
  "text/css",
  "application/json",
  "application/yaml",
  "application/x-yaml",
  "application/xml",
  "application/csv",
]);

export const SUPPORTED_IMAGE_MIMETYPES: ReadonlySet<string> = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

export const SUPPORTED_PDF_MIMETYPES: ReadonlySet<string> = new Set(["application/pdf"]);

export const TEXT_FILE_EXTENSIONS: ReadonlySet<string> = new Set([
  ".log", ".txt", ".csv", ".json", ".yaml", ".yml", ".xml", ".md",
  ".py", ".js", ".ts", ".jsx", ".tsx", ".sh", ".bash", ".zsh", ".rb",
  ".go", ".rs", ".java", ".kt", ".swift", ".c", ".cpp", ".h", ".hpp",
  ".css", ".html", ".htm", ".sql", ".toml", ".ini", ".cfg", ".conf",
  ".env", ".tf", ".hcl", ".dockerfile", ".graphql", ".gql", ".proto",
]);

export const IMAGE_FILE_EXTENSIONS: ReadonlySet<string> = new Set([
  ".jpg", ".jpeg", ".png", ".gif", ".webp",
]);

export const PDF_FILE_EXTENSIONS: ReadonlySet<string> = new Set([".pdf"]);

const fileExt = (name: string): string => {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
};

export const isSupportedAttachment = (mimetype: string, filename = ""): boolean => {
  if (SUPPORTED_TEXT_MIMETYPES.has(mimetype) || mimetype.startsWith("text/")) return true;
  if (SUPPORTED_IMAGE_MIMETYPES.has(mimetype)) return true;
  if (SUPPORTED_PDF_MIMETYPES.has(mimetype)) return true;
  const ext = fileExt(filename);
  if (ext) {
    if (TEXT_FILE_EXTENSIONS.has(ext)) return true;
    if (IMAGE_FILE_EXTENSIONS.has(ext)) return true;
    if (PDF_FILE_EXTENSIONS.has(ext)) return true;
  }
  return false;
};

// react-dropzone `accept` map: mimetype → list of file extensions (or [] for "any").
// Lists known images and PDF explicitly; lists "text/*" with the long extension list
// so dropzone allows all the text-ish files we accept on the server.
export const DROPZONE_ACCEPT: Record<string, string[]> = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
  "text/*": Array.from(TEXT_FILE_EXTENSIONS),
  "application/json": [".json"],
  "application/yaml": [".yaml", ".yml"],
  "application/xml": [".xml"],
};
